import { useDataValue, useDataChange } from 'Simple/Data.js'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import { useMutation } from 'urql'
import bootstrapMutation from './mutation-create-lead-person.graphql.js'
import authorizeMutation from './mutation-authorize-card.graphql.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, executeBootstrap] = useMutation(bootstrapMutation)
  let [, executeAuthorize] = useMutation(authorizeMutation)
  let [, notify] = useNotifications()

  let questionId = useDataValue({
    context: 'question',
    path: 'id',
    viewPath: props.viewPath,
  })

  let allowSkipOnDecline = useDataValue({
    context: 'question',
    path: 'allowSkipOnDecline',
    viewPath: props.viewPath,
  })

  let changeSelected = useDataChange({
    context: 'selected',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)

  return async function onSubmit({ args, change }) {
    switch (args?.type) {
      case 'bootstrap': {
        change(next => {
          next.status = 'loading'
          next.error = null
        })

        let response = await executeBootstrap()

        if (response.error) {
          change(next => {
            next.status = 'error'
            next.error =
              response.error.graphQLErrors?.[0]?.message ||
              response.error.message ||
              'Failed to prepare credit card capture'
          })
          return
        }

        let { person, responsible_person, location_id } =
          response.data.leads_create_lead_person

        change(next => {
          next.status = 'ready'
          next.person = person
          next.responsible_person = responsible_person
          next.effective_person = responsible_person ?? person
          next.location_id = location_id
        })
        return
      }

      case 'authorize_card': {
        let { token } = args

        let response = await executeAuthorize({ token })
        if (response.error) {
          let message =
            response.error.graphQLErrors?.[0]?.message ||
            response.error.message ||
            'Could not authorize the card.'
          if (!allowSkipOnDecline) {
            notify(notifyError(message))
            return
          }
          notify(notifyError(`${message} Continuing without a saved card.`))
          advance({ skipped: true, reason: 'error' })
          return
        }

        let result = response.data?.leads_authorize_card
        if (!result?.verified) {
          let message =
            result?.verification_metadata?.auth_message ||
            'The card could not be authorized. Please try a different card.'
          if (!allowSkipOnDecline) {
            notify(notifyError(message))
            return
          }
          notify(notifyError(`${message} Continuing without a saved card.`))
          advance({ skipped: true, reason: 'declined' })
          return
        }

        advance({ token_id: result.token_id, verified: true })
        return
      }

      default: {
        throw new Error(`Unknown credit_card_capture action ${args?.type}`)
      }
    }

    function advance(answer) {
      changeSelected(next => {
        next.answers[questionId] = answer

        let nextIndex = next.flowQuestions.findIndex(q => q === questionId) + 1
        if (nextIndex < next.flowQuestions.length) {
          next.current_question_id = next.flowQuestions[nextIndex]
        } else {
          setFlowTo(normalizePath(props.viewPath, '../../../Review'))
        }
      })
    }
  }
}
